import {apiClient as client} from './apiClient'
import {AxiosResponse} from 'axios'
import {
    ActionResponse,
    AddSubmissionRequest,
    AddSubmissionResponse,
    AvailabilityConsumer,
    AvailabilityConsumerKey,
    CheckDynamicExecutionResponse,
    CheckExclusionGroupResponse,
    CheckRuleGroupResponse,
    DynamicExecutionsHistory,
    GetPermissionsResponse,
    Iap,
    IapMetadataKeyObject,
    IapMetadataQueryResponse,
    IapRelationship,
    IapRelationshipWithNames,
    InputQuery,
    Iog,
    Iogl,
    IogMetadataKeyObject,
    IogMetadataQueryResponse,
    MetadataQuery,
    OutputQuery,
    ProgramResponse,
    SearchExclusionGroupsResponse,
    SearchRuleGroupsResponse,
    StartWorkflowResponse,
    SubmissionDetails,
    WorkflowExecutionDetails,
    WorkflowExecutionHistory,
    WorkflowExecutionStatus,
    WorkflowsResponse,
} from './model'
import * as queryString from 'query-string'
import {convertQueryToString} from '../components/search/SearchUtils'

const options = {skipEmptyString: true}

export async function fetchIapDetails(iapId: string): Promise<AxiosResponse<Iap>> {
    return client.get(`/iaps/${iapId}`)
}

export async function fetchIapRelationships(iapId: string): Promise<AxiosResponse<IapRelationship[]>> {
    return client.get(`/iaps/${iapId}/iap-relationships`)
}

export async function fetchIapRelationshipsWithNamesByIaps(iapId: string): Promise<AxiosResponse<IapRelationshipWithNames[]>> {
    return client.get(`/iaps/${iapId}/iap-relationships-with-names`)
}

export async function fetchIoglsByIap(iapId: string): Promise<AxiosResponse<Iogl[]>> {
    return client.get(`/iaps/${iapId}/iogls`)
}

export async function fetchIogsByIap(iapId: string): Promise<AxiosResponse<Iog[]>> {
    return client.get(`/iaps/${iapId}/iogs`)
}

export async function fetchAcIdsByIapAndFnsku(iapId: string, fnsku = ''): Promise<AxiosResponse<string[]>> {
    const config = fnsku ? {params: {'fnsku': fnsku}} : undefined
    return client.get(`/iaps/${iapId}/availability-consumer-ids`, config)
}

export async function fetchAcDetailsByIapAndFnsku(iapId: string, fnsku = ''): Promise<AxiosResponse<AvailabilityConsumer[]>> {
    const config = fnsku ? {params: {'fnsku': fnsku}} : undefined
    return client.get(`/iaps/${iapId}/availability-consumers`, config)
}

export async function queryIapsByMetadata(query: MetadataQuery): Promise<AxiosResponse<IapMetadataQueryResponse>> {
    const config = query ? {params: {...convertQueryToString(query)}} : undefined
    return client.get(`/iaps`, config)
}

export async function fetchAcDetailsById(acId: string): Promise<AxiosResponse<AvailabilityConsumerKey>> {
    return client.get(`/availability-consumers/${acId}`)
}

export async function fetchAcIdByKey(
    marketplaceId: string, merchantCustomerId: string, program = '', region = '',
): Promise<AxiosResponse<string>> {
    // If optional params are empty, don't pass them in
    const programParam = program ? {'fulfillmentProgramId': program} : ''
    const regionParam = region ? {'fulfillmentRegionId': region} : ''

    const config = {
        params: {
            'marketplaceId': marketplaceId,
            'merchantCustomerId': merchantCustomerId,
            ...programParam,
            ...regionParam,
        },
    }

    return client.get('/availability-consumers', config)
}

export async function fetchProgramById(programId: string): Promise<AxiosResponse<ProgramResponse>> {
    return client.get(`/program/${programId}`)
}

export async function fetchIapByAcAndFnsku(acId: string, fnsku = ''): Promise<AxiosResponse<string>> {
    const config = fnsku ? {params: {'fnsku': fnsku}} : undefined
    return client.get(`/availability-consumers/${acId}/iaps`, config)
}

export async function fetchIogDetails(iogId: string): Promise<AxiosResponse<Iog>> {
    return client.get(`/iogs/${iogId}`)
}

export async function queryIogsByMetadata(query: MetadataQuery): Promise<AxiosResponse<IogMetadataQueryResponse>> {
    const config = query ? {params: {...convertQueryToString(query)}} : undefined
    return client.get(`/iogs`, config)
}

export async function fetchIapsByIog(iogId: string): Promise<AxiosResponse<Iap[]>> {
    return client.get(`/iogs/${iogId}/iaps`)
}

export async function fetchIoglDetails(iogId: string, fc: string): Promise<AxiosResponse<Iogl>> {
    return client.get(`/iogs/${iogId}/fcs/${fc}`)
}

export async function fetchIapsByIogl(iogId: string, fc: string): Promise<AxiosResponse<Iap[]>> {
    return client.get(`/iogs/${iogId}/fcs/${fc}/iaps`)
}

export async function fetchIoglsByIog(iogId: string): Promise<AxiosResponse<Iogl[]>> {
    return client.get(`/iogs/${iogId}/iogls`)
}

export async function startAmosWorkflow(
    workflowName: string,
    content: string,
    region: string,
): Promise<AxiosResponse<StartWorkflowResponse>> {
    return client.post(`/workflows/${workflowName}?region=${region}`, content)
}

export async function startBatchAmosWorkflow(
    workflowName: string,
    content: string,
    region: string,
): Promise<AxiosResponse<StartWorkflowResponse[]>> {
    return client.post(`/workflows/${workflowName}/batch?region=${region}`, content)
}

export async function fetchWorkflowExecutionHistory(
    executionId?: string
): Promise<AxiosResponse<WorkflowExecutionDetails>> {
    return client.get(`/executions/${executionId}`)
}

export async function fetchWorkflowExecutionsHistory(
    pageSize: number,
    pageNum: number,
    workflowName?: string,
    status?: WorkflowExecutionStatus,
    requester?: string,
    fromCreationTime?: string,
    toCreationTime?: string,
    inputQuery?: InputQuery,
    outputQuery?: OutputQuery,
): Promise<AxiosResponse<WorkflowExecutionHistory>> {
    const maxResults = pageSize.toString()
    const page = pageNum.toString()

    const basicQuery = {
        maxResults,
        page,
        workflowName,
        status,
        requester,
        fromCreationTime,
        toCreationTime,
    }
    const advancedQuery = {
        ...basicQuery,
        inputQueryString: inputQuery && JSON.stringify(convertQueryToString(inputQuery)),
        outputQueryString: outputQuery && JSON.stringify(convertQueryToString(outputQuery)),
    }

    const url = queryString.stringifyUrl({
        url: '/executions',
        query: (inputQuery && Object.keys(inputQuery).length > 1) ||
            (outputQuery && Object.keys(outputQuery).length > 1) ? advancedQuery : basicQuery,
    }, options)

    const response = await client.get(url)

    return {
        ...response,
        data: {
            ...response.data,
            executions: response.data.executions.map((execution: WorkflowExecutionDetails) => ({
                executionId: execution.executionId,
                workflowName: execution.workflowName,
                status: execution.status,
                startedAt: new Date(execution.createTime),
                lastModifiedAt: new Date(execution.lastModifiedTime),
                startedBy: !execution.requester.actorId ? execution.requester.applicationName : execution.requester.actorId,
                region: execution.region ?? '',
            })),
            count: response.data.totalExecutions,
        },
    }
}

export async function fetchWorkflows(actorId?: string, actorType?: string): Promise<AxiosResponse<WorkflowsResponse>> {
    const url = queryString.stringifyUrl({
        url: '/workflows',
        query: {
            actorId,
            actorType,
        },
    }, options)

    return client.get(url)
}

export async function fetchDynamicExecutions(): Promise<AxiosResponse<DynamicExecutionsHistory>> {
    return client.get('/dynamic/executions')
}

export async function fetchDynamicExecutionDetails(executionId: String): Promise<AxiosResponse<CheckDynamicExecutionResponse>> {
    return client.get(`/dynamic/executions/${executionId}`)
}

export async function fetchRuleGroups(): Promise<AxiosResponse<SearchRuleGroupsResponse>> {
    return client.get('/ruleGroups')
}

export async function fetchRuleGroupDetails(ruleGroupName: String): Promise<AxiosResponse<CheckRuleGroupResponse>> {
    return client.get(`/ruleGroups/${ruleGroupName}`)
}

export async function fetchExclusionGroups(): Promise<AxiosResponse<SearchExclusionGroupsResponse>> {
    return client.get('/exclusionGroups')
}

export async function fetchExclusionGroupDetails(exclusionGroupName: String): Promise<AxiosResponse<CheckExclusionGroupResponse>> {
    return client.get(`/exclusionGroups/${exclusionGroupName}`)
}

export async function fetchActions(): Promise<AxiosResponse<ActionResponse>> {
    const url = queryString.stringifyUrl({
        url: '/apabrewery/actions',
    }, options)

    return client.get(url)
}

export async function createSubmission(content: AddSubmissionRequest): Promise<AxiosResponse<AddSubmissionResponse>> {
    const url = queryString.stringifyUrl({
        url: '/apabrewery/submissions',
    }, options)

    return client.post(url, content)
}

export async function getSubmission(id: string): Promise<AxiosResponse<SubmissionDetails>> {
    const url = queryString.stringifyUrl({
        url: `/apabrewery/submissions/${id}`,
    }, options)

    return client.get(url)
}

export async function updateSubmission(id: string, content: SubmissionDetails): Promise<AxiosResponse> {
    const url = queryString.stringifyUrl({
        url: `/apabrewery/submissions/${id}`,
    }, options)

    return client.put(url, content)
}

export async function executeSubmission(id: string): Promise<AxiosResponse> {
    const url = queryString.stringifyUrl({
        url: `/apabrewery/executions/${id}`,
    }, options)

    return client.put(url)
}

export async function getPermissions(): Promise<AxiosResponse<GetPermissionsResponse>> {
    const url = queryString.stringifyUrl({
        url: `/permissions`,
    }, options)

    return client.get(url)
}

export async function fetchIogMetadataKeys(): Promise<AxiosResponse<IogMetadataKeyObject[]>> {
    return client.get("/iogs/metadataKeys")
}

export async function fetchIapMetadataKeys(): Promise<AxiosResponse<IapMetadataKeyObject[]>> {
    return client.get("/iaps/metadataKeys")
}
